import DashboardLayout from "../../../lib/components/layouts/dashboard";
import { Col, Row } from "react-bootstrap";
import AppointmentCalender from "../../../lib/components/appointments/appointment-calender";
import moment from "moment";
import { momentLocalizer } from "react-big-calendar";
// import { APPOINTMENTS_LIST } from "lib/data/dummy-appointments";
import { useEffect, useMemo, useState } from "react";
import FlexBox from "lib/components/common/flex-box";
import CalendarFilters from "lib/components/common/calendar-filter";
import useClinics from "lib/hooks/useClinics";
import LoadingBox from "lib/components/common/loading-box";
import ErrorBox from "lib/components/common/error-box";
import ClinicWidget from "lib/components/common/dashboard/clinic-widget";
import { getCalendarEventsFor, getFullName } from "lib/utils/util";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { UNAVAILABLE_DAYS } from "lib/constants";
import useRooms from "lib/hooks/useRooms";
import { DEFAULT_ROOM_DETAILS } from "lib/constants/default-values";
import usePractitionerSubscriptions from "lib/hooks/usePractitionerSubscriptions";
import SimpleCard from "lib/components/common/simple-card";
import ClinicRoomsDropdown from "lib/components/common/dashboard/clinic-rooms-dropdown";
import ROOM_BOOKING_SERVICE from "lib/services/room-booking-service";
import usePractitioner from "lib/hooks/usePractitioner";

const localizer = momentLocalizer(moment);

const BreadcrumbItems = [
  {
    label: "Dashboard",
    path: PAGE_ROUTES.ADMIN.Dashboard,
  },
  {
    label: "Calendar",
    path: PAGE_ROUTES.ADMIN.Calendar,
  },
];

const AdminCalendar = () => {
  const { clinics, getClinics, loading, error } = useClinics();
  const { getRooms, loading: roomsLoading, error: roomsError } = useRooms();
  const {
    getSubscriptionsByClinic,
    loading: practitionerSubscriptionsLoading,
    error: practitionerSubscriptionsError,
  } = usePractitionerSubscriptions();
  const { getPractitionerDetails } = usePractitioner();

  const [showCancellations, setShowCancellations] = useState(true);
  const [showHolidays, setShowHolidays] = useState(true);
  const [showUnavailable, setShowUnavailable] = useState(true);
  const [showPractitionerBookings, setShowPractitionerBookings] =
    useState(true);
  const [selectedClinic, setSelectedClinic] = useState<ClinicDetails>(null);
  const [clinicRooms, setClinicRooms] = useState<RoomDetails[]>([]);
  const [selectedClinicRoom, setSelectedClinicRooms] =
    useState<RoomDetails>(DEFAULT_ROOM_DETAILS);
  const [clinicSubscriptions, setClinicSubscriptions] = useState<
    PractitionerSubscriptionDetails[]
  >([]);
  const [practitionersById, setPractitionersById] = useState<
    Record<string, PractitionerDetails>
  >({});

  useEffect(() => {
    getClinics(false);
  }, []);

  useEffect(() => {
    if (!selectedClinic?.clinic?.id) return;

    // Fetch clinic rooms
    getRooms(selectedClinic.clinic.id, (rooms) => {
      setClinicRooms(rooms);
      setSelectedClinicRooms(rooms?.[0]);
    });

    // Fetch subscriptions and practitioners
    getSubscriptionsByClinic(selectedClinic.clinic.id, (subs) => {
      setClinicSubscriptions(subs);

      subs.forEach((sub) => {
        getPractitionerDetails(sub.practitionerId, (data) => {
          if (!data?.id) return;

          // Append to existing state — not overwrite
          setPractitionersById((prev) => ({
            ...prev,
            [data.id]: data,
          }));
        });
      });
    });
  }, [selectedClinic?.clinic?.id]);

  /** Calculated Vars */
  const practitionerBookingEvents = useMemo(
    () =>
      clinicSubscriptions.flatMap((sub) => {
        const practitioner = practitionersById[sub.practitionerId];

        const fullName = practitioner
          ? getFullName(practitioner.firstName, practitioner.lastName)
          : "Practitioner Booking";

        const dates = ROOM_BOOKING_SERVICE.getUnavailableRoomDates(
          [sub],
          selectedClinicRoom?.id
        );

        return getCalendarEventsFor(
          dates,
          UNAVAILABLE_DAYS.PRACTITIONER_BOOKINGS,
          `${fullName} - Room-${sub.room}`
        );
      }),
    [clinicSubscriptions, selectedClinicRoom]
  );
  /** Calculated Vars */

  return (
    <DashboardLayout breadcrumbItems={BreadcrumbItems}>
      {loading || practitionerSubscriptionsLoading || roomsLoading ? (
        <LoadingBox />
      ) : (
        <>
          {(error || practitionerSubscriptionsError || roomsError) && (
            <ErrorBox message={error} />
          )}
          <Row className="overflow-auto mt-4 custom-scroll">
            {(clinics ?? []).map((clinic, index) => (
              <Col sm="3" key={clinic?.id}>
                <ClinicWidget
                  clinic={clinic}
                  className="cursor-pointer hover-shadow"
                  onSelected={(value) => {
                    setSelectedClinic(value);
                  }}
                  isSelected={selectedClinic?.clinic?.index - 1 === index}
                />
              </Col>
            ))}
          </Row>
        </>
      )}

      {selectedClinic && (
        <SimpleCard
          title={selectedClinic?.clinic?.name}
          action={
            <ClinicRoomsDropdown
              isShowAll
              clinicRooms={clinicRooms}
              selectedRoom={selectedClinicRoom}
              onRoomChange={setSelectedClinicRooms}
            />
          }
        >
          <AppointmentCalender
            holidayList={getCalendarEventsFor(
              selectedClinic?.calendar?.holidays ?? [],
              UNAVAILABLE_DAYS.HOLIDAY
            )}
            unavailableDays={getCalendarEventsFor(
              selectedClinic?.calendar?.unavailableDates ?? [],
              UNAVAILABLE_DAYS.UNAVAILABLE
            )}
            practitionerBookings={practitionerBookingEvents}
            calendarFilters={
              <CalendarFilters
                showCancellations={showCancellations}
                setShowCancellations={setShowCancellations}
                showHolidays={showHolidays}
                setShowHolidays={setShowHolidays}
                showUnavailable={showUnavailable}
                setShowUnavailable={setShowUnavailable}
                showPractitionerBookings={showPractitionerBookings}
                setShowPractitionerBookings={setShowPractitionerBookings}
                appointmentRoute={PAGE_ROUTES.PRACTITIONER.BookAppointment}
              />
            }
            localizer={localizer}
            showHolidays={showHolidays}
            showUnavailable={showUnavailable}
            showBookings={showPractitionerBookings}
          />
        </SimpleCard>
      )}
    </DashboardLayout>
  );
};

export default AdminCalendar;
